import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchDetail, usePutDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { ActionsType } from "src/interfaces/Common";
import { MethodType } from "src/services/type";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { Group } from "src/interfaces/Group";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ButtonBackPage from "@components/ButtonBackPage";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { isTrueValue } from "@utils/utilsValidators";

const Attributes: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
  }>();
  const [deletedId, setDeleteId] = useState<any>(null);
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_groups_groups`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable<Group[]>(
      ServicesURL.attributes + `?corporateId=${id}`,
      PagesID["corporate.attributes"],
      filters[filterId],
    );

  const { trigger: triggerPutAttributes, setId: setPutGroupId } = usePutDetail(
    ServicesURL.attributes,
  );

  const tableToolbarActions = useMemo(() => {
    return actionsTopToolbar?.map((action) => ({
      ...action,
      disabled:
        action.type === "add" && dataTable?.enrollmentResponses?.length >= 6,
    }));
  }, [actionsTopToolbar, dataTable]);

  const { t } = useTranslation(["attributes-page", "modal"]);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );

  const { trigger: triggerDelete } = useSWRMutation(
    deletedId ? `${ServicesURL.attributes}/${deletedId}` : null,
    (url, params) => {
      return fetcher<any>(url, MethodType.DELETE, params.arg);
    },
  );

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (el: { attributeId: string }) => {
              setDeleteId(el.attributeId);
              setOpen(true);
            },
          };
        }
        case "edit":
          return {
            ...action,
            callBack: (row: any) =>
              navigate(
                `/corporate/${id}/detail/attributes/edit/${row.attributeId}`,
              ),
          };
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const handleDelete = React.useCallback(async () => {
    setOpenToast({ s: StatusEnum.LOADING });
    let deleteResponse = undefined;
    try {
      deleteResponse = await triggerDelete();
    } catch (error) {
      setOpenToast(undefined);
    }

    if (deleteResponse) {
      await mutate(ServicesURL.staticGroups + `/static?id_corporate=${id}`);
      setOpenToast({ s: StatusEnum.OK });
    }
  }, [mutate, id, triggerDelete]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-initiatives.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataDetail?.name || ""} - ${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id="groups_groups"
            data={dataTable?.enrollmentResponses}
            columns={columns || []}
            configurationTopToolbar={tableToolbarActions}
            configurationActionForRow={actionsRow}
            totalPages={dataTable?.metadata?.totalElements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
            enableRowDrag={true}
            initialState={{
              sorting: [
                {
                  id: "order", //sort by order by default on page load
                  desc: false,
                },
              ],
            }}
            onDragChange={async (
              draggingElements: any,
              hoveredElements: any,
            ) => {
              await setPutGroupId(draggingElements.attributeId);
              const explore =
                isTrueValue(draggingElements.exploreCarousel) || false;
              const forY = isTrueValue(draggingElements.forYou) || false;
              const order = Number(hoveredElements.orderParam);
              let objToSend: any = {
                attribute: draggingElements.attribute,
                corporateId: Number(id),
                forYou: forY,
                exploreCarousel: explore,
                orderParam: order,
              };

              try {
                await triggerPutAttributes(objToSend);
                await mutate(ServicesURL.attributes + `?corporateId=${id}`);
              } catch (error) {
                console.error(error);
              }
            }}
          />
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!open}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpen(false);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default Attributes;
